import { HttpClient, HttpParams } from '@angular/common/http';
import { inject, Injectable } from '@angular/core';
import { CustomEncoder, DynamicDataService, ENVIRONMENT } from '@iot-platform/core';
import { ApiHelpers } from '@iot-platform/iot-platform-utils';
import { CommonApiRequestBody, DynamicDataResponse, Environment } from '@iot-platform/models/common';
import { get } from 'lodash';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { IotMapRequest, IotGeoJsonFeature, IotGeoJsonFeatureCollection, IotGeoJsonRouteFeature } from '@iot-platform/models/maps';

@Injectable({
  providedIn: 'root'
})
export class MapService {
  private readonly environment: Environment = inject(ENVIRONMENT);
  private readonly httpClient: HttpClient = inject(HttpClient);
  private readonly dynamicDataService: DynamicDataService = inject(DynamicDataService);

  loadGeoLocations(request: IotMapRequest): Observable<IotGeoJsonFeature[]> {
    const params: HttpParams = new HttpParams({ encoder: new CustomEncoder() });
    const body: CommonApiRequestBody = ApiHelpers.getHttpRequestBody(request);
    if (request.options) {
      body.options = request.options;
    }

    return this.httpClient
      .post<IotGeoJsonFeatureCollection>(`${this.environment.api.url}${this.environment.api.endpoints.worldMap}/${request.concept}/search`, body, { params })
      .pipe(map((response: IotGeoJsonFeatureCollection) => response.features as IotGeoJsonFeature[]));
  }

  getFieldId(concept: string): string {
    if (concept === 'devices') {
      return 'deviceId';
    } else if (concept === 'assets') {
      return 'assetId';
    }
    return 'siteId';
  }

  loadMarkerDetails(concept: string, feature: IotGeoJsonFeature): Observable<DynamicDataResponse> {
    const fieldId: string = this.getFieldId(concept);
    return this.dynamicDataService.getDynamicData(concept, {
      useCache: true,
      cacheFieldId: fieldId,
      pathParams: [fieldId],
      rawData: { [fieldId]: get(feature, 'properties.id', '') }
    });
  }

  loadRoute(assetId: string, limit: number, start: Date, end: Date, minutesBetweenSegments: number): Observable<IotGeoJsonRouteFeature[]> {
    return this.httpClient
      .get<IotGeoJsonFeatureCollection>(
        `${this.environment.api.url}${
          this.environment.api.endpoints['assets']
        }/${assetId}/geojson?limit=${limit}&minutesBetweenSegments=${minutesBetweenSegments}&start=${start.toISOString()}&end=${end.toISOString()}&format=segments`,
        {}
      )
      .pipe(map((response: any) => response.features));
  }
}
